import { createEffect } from '../utils/create-effect';

import { APP_ELEMENT_ID, SCROLLABLE_INNER_CONTAINER_ID } from './constants';
import { FocusRenderer } from './renderers/focus-renderer';
import { createHighlightsContainerElement } from './utils/create-dom-elements';

/**
 * Creates the main effect that draws / renders highlights, focus, etc.
 */
export const createHighlightsEffect = createEffect(() => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------

  /**
   * The internal highlights container, where focus, highlights, etc. are drawn and positioned.
   */
  let highlightsContainer: HTMLElement | null = null;

  /**
   * The el with the id SCROLLABLE_INNER_CONTAINER_ID, which is the main
   * scrolling container on the databases app, rendered in react.
   */
  let scrollableContainer: HTMLElement | null = null;

  /**
   * The mutation observer, which listens to the DOM and waits for the
   * el with the id SCROLLABLE_INNER_CONTAINER_ID to mount. Without this,
   * the highlightsContainer has nothing to attach itself to.
   */
  let observer: MutationObserver | null = null;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /**
   * We modify the position to be relative when this setup() is run.
   * We cache the original so we can roll it back if there was one.
   */
  let originalPosition: string | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------

  /**
   * The renderer that is responsible for creating, updating, and removing the blue
   * focus box.
   */
  let focusRenderer: FocusRenderer | null = null;

  function attemptMountingHighlightsContainerToScrollContainer() {
    if (!highlightsContainer) {
      return;
    }

    const scrollableInnerContainer = document.getElementById(
      SCROLLABLE_INNER_CONTAINER_ID
    );

    if (
      scrollableInnerContainer &&
      highlightsContainer.parentElement !== scrollableInnerContainer
    ) {
      originalPosition = scrollableInnerContainer.style.position || null;
      scrollableInnerContainer.style.position = 'relative';
      scrollableInnerContainer.appendChild(highlightsContainer);
      scrollableContainer = scrollableInnerContainer;

      // Initialize the focusRenderer now that we have the els
      focusRenderer = new FocusRenderer(highlightsContainer, scrollableContainer);
    }
  }

  return {
    deltaTypes: ['selectionModified', 'selectionReplaced', 'focusChanged'],

    setup() {
      // create the core elements in memory
      highlightsContainer = createHighlightsContainerElement();

      // setup the MutationObserver to attempt attaching / mounting our highlights container
      observer = new MutationObserver(() =>
        attemptMountingHighlightsContainerToScrollContainer()
      );

      // kick off the observer / watch for changes
      const listenerRoot = document.getElementById(APP_ELEMENT_ID) || document.body;

      observer.observe(listenerRoot, { childList: true, subtree: true });

      // attempt to mount (in case React is already ready)
      attemptMountingHighlightsContainerToScrollContainer();
    },

    dispose() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      // Clean up the focusRenderer and its elements
      if (focusRenderer) {
        focusRenderer.clear();
        focusRenderer = null;
      }

      if (highlightsContainer) {
        highlightsContainer.remove();
        highlightsContainer = null;
      }

      if (scrollableContainer) {
        if (originalPosition === null) {
          scrollableContainer.style.removeProperty('position');
        } else {
          scrollableContainer.style.position = originalPosition;
        }

        scrollableContainer = null;
        originalPosition = null;
      }
    },

    onDelta(delta) {
      // We need the manager for selections, and the elements for focus.
      if (!focusRenderer || !scrollableContainer) {
        return;
      }

      // Handle Focus (The Blue Border)
      // Focus can change on its own event, or as part of a selection replacement
      const focusChange = delta.kind === 'focusChanged' ? delta : delta.focusChange;

      if (focusChange) {
        focusRenderer.update(focusChange.next);
      }
    },
  };
});
